import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TestChartItem } from '@/types/testReport';
import { parseDecimal, toPercentage } from '@/utils/text';

const ZERO_BAR_HEIGHT = '2px';

interface Props {
  items: TestChartItem[];
}

const withCurrentUser = (items: TestChartItem[]): TestChartItem[] => {
  const hasCurrentUser = items.some(({ isCurrentUser }) => isCurrentUser);

  if (hasCurrentUser) {
    return items;
  }

  return [
    ...items,
    { value: '0', place: items.length + 1, isCurrentUser: true },
  ];
};

export const TestQuestionChart: React.FC<Props> = ({ items }) => {
  const { t } = useTranslation();

  const bars = withCurrentUser(items);
  const values = bars.map(({ value }) => parseDecimal(value));
  const maxValue = Math.max(...values);
  const averageValue =
    values.reduce((total, value) => total + value, 0) / values.length;
  const hasPoints = maxValue > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="relative mt-7 flex h-[100px] items-end gap-1">
        {bars.map((bar, index) => {
          const value = values[index];
          const height = hasPoints
            ? toPercentage((value / maxValue) * 100)
            : ZERO_BAR_HEIGHT;

          return (
            <span
              key={index}
              style={{ height: value > 0 ? height : ZERO_BAR_HEIGHT }}
              className={`relative flex-1 rounded ${bar.isCurrentUser ? 'bg-accent' : 'bg-on-primary-surface'}`}
            >
              {bar.isCurrentUser && hasPoints && (
                <span className="text-caption-10 absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 rounded bg-accent px-1 py-0.5 font-semibold whitespace-nowrap text-accent-foreground">
                  {t('overview.ratings.ranking.place', { place: bar.place })}
                </span>
              )}
            </span>
          );
        })}

        {hasPoints && (
          <span
            style={{ bottom: toPercentage((averageValue / maxValue) * 100) }}
            className="pointer-events-none absolute inset-x-0 border-t border-dashed border-accent"
          />
        )}
      </div>

      <div className="flex items-center justify-end gap-1">
        <span className="h-0 w-6 border-t border-dashed border-accent" />
        <span className="text-body-14 text-subtle-foreground">
          {t('diary.testReport.classAverage')}
        </span>
      </div>
    </div>
  );
};
